import React, { useEffect, useState } from 'react'
import useScrollToTop from '../../../hooks/useScrollToTop'
import { Grid, GridColumn, GridRow } from 'semantic-ui-react'
import { useSelector } from 'react-redux'
import RegisteredFormField from '../../common/RegisteredFormField'
import { DISABILITY_TYPE } from '../../../constant/DISABILITY_TYPE'
import Message from '../../common/Message'
import Card from '../../common/Card'
import CurrencyInput from '../common/inputs/CurrencyInput'

export const CONFIRM_TOTAL_AMOUNTS = 'The values provided above have been added together. Please confirm this total reflects the total amount of insurance you would like to have. If not, adjust the values accordingly.'
export const INPUT_INSTRUCTION = 'Indicate the <--amountType--> amount of insurance for yourself and your dependents (if any)'
export const CURRENT_AMOUNT_INSTRUCTION = INPUT_INSTRUCTION.replace('<--amountType-->', 'current')
export const ADDITIONAL_AMOUNT_INSTRUCTION = INPUT_INSTRUCTION.replace('<--amountType-->', 'additional')
export const LIFE_AMOUNTS = 'Life Insurance:'
export const CI_AMOUNTS = 'Critical Illness/Specified Disease Insurance:'
export const HINTS = [
  'If you recently enrolled for insurance and are applying for coverage in excess of the Guarantee Issue amount, the Guarantee Issue amount is the current amount you should enter.',
  'If you (or a dependent) do not currently have coverage, enter 0 (zero).',
  'If you have had insurance for some time, and are applying to increase the amount of coverage you have, enter the current amount of coverage you have.'
]

export default function Coverage () {
  const {
    life: { value: life },
    ci: { value: ci },
    disability: { value: disability },
    applicantType: { value: applicantType }
  } = useSelector(state => state.form.options)

  const { spouseApplying, childrenApplying } = useSelector(state => state.form.segments.dependents.fields)
  const [applicants, setApplicants] = useState([])

  const lifeAndCiHeader = () => {
    return [
      life && 'Life Insurance',
      (life && ci) && 'and',
      ci && 'Critical Illness/Specified Disease Insurance',
      'Benefit Amount'
    ]
      .filter(Boolean)
      .join(' ')
  }

  useEffect(() => {
    setApplicants([
      { value: 'employee', label: 'Employee' },
      spouseApplying.value === 'yes' && { value: 'spouse', label: 'Spouse' },
      childrenApplying.value === 'yes' && { value: 'children', label: 'Each Child' }
    ].filter(Boolean))
  }, [spouseApplying, childrenApplying])

  return useScrollToTop(<>
    {disability && <Grid container stackable divided='vertically'>
      <GridRow>
        <GridColumn width={16}>
          <h3>Disability Coverage</h3>
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn computer={9} tablet={16} mobile={16}>
          <RegisteredFormField
            label='Indicate the type of coverage you are applying for'
            placeholder='Select one'
            id='disability-type'
            inputType='select'
            options={DISABILITY_TYPE}
            path='segments.coverageAmount.fields.employee.disabilityType'
          />
        </GridColumn>
      </GridRow>
    </Grid>}
    {(life || ci) && <Grid container stackable divided='vertically'>
      <GridRow>
        <GridColumn width={16}>
          <h3>{lifeAndCiHeader()}</h3>
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn width={16}>
          <p><i>{CURRENT_AMOUNT_INSTRUCTION}</i></p>
        </GridColumn>
        {life && <>
          <GridColumn width={16}>
            <h5>{LIFE_AMOUNTS}</h5>
          </GridColumn>
          {applicants.map(a => {
            return <GridColumn key={`${a.value}-current-life`} width={5}>
              <CurrencyInput
                id={`${a.value}LifeCurrent`}
                path={`segments.coverageAmount.fields.${a.value}.lifeCurrent`}
                label={a.label}
              />
            </GridColumn>
          })}
        </>}
        {ci && <>
          <GridColumn width={16}>
            <h5>{CI_AMOUNTS}</h5>
          </GridColumn>
          {applicants.filter(a => ['employee', 'spouse'].includes(a.value)).map(a => {
            return <GridColumn key={`${a.value}-current-ci`} width={5}>
              <CurrencyInput
                id={`${a.value}CiCurrent`}
                path={`segments.coverageAmount.fields.${a.value}.ciCurrent`}
                label={a.label}
              />
            </GridColumn>
          })}
        </>}
        <GridColumn width={16}>
          <Message
            content='Helpful Hints'
            hoverContent={<ul>
              {HINTS.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>}
          />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn width={16} style={{ marginTop: '1.5rem!important' }}>
          <p><i>{ADDITIONAL_AMOUNT_INSTRUCTION}</i></p>
        </GridColumn>
        {life && <>
          <GridColumn width={16}>
            <h5>{LIFE_AMOUNTS}</h5>
          </GridColumn>
          {/* Placeholder column for employee when applicantType is SP_ONLY */}
          {applicantType === 'SP_ONLY' && (
              <GridColumn width={5}>
              </GridColumn>
          )}
          {applicants.filter(a => applicantType !== 'SP_ONLY' || a.value !== 'employee').map(a => {
            return (
                <GridColumn key={`${a.value}-additional-life`} width={5}>
                  <CurrencyInput
                      id={`${a.value}LifeAdditional`}
                      path={`segments.coverageAmount.fields.${a.value}.lifeAdditional`}
                      label={a.label}
                  />
                </GridColumn>
            )
          })}
        </>}
        {ci && <>
          <GridColumn width={16}>
            <h5>{CI_AMOUNTS}</h5>
          </GridColumn>
          {/* Placeholder column for employee when applicantType is SP_ONLY */}
          {applicantType === 'SP_ONLY' && (
              <GridColumn width={5}></GridColumn>
          )}
          {applicants.filter(a => a.value !== 'children' && (applicantType !== 'SP_ONLY' || a.value !== 'employee')).map(a => {
            return <GridColumn key={`${a.value}-additional-ci`} width={5}>
              <CurrencyInput
                  id={`${a.value}CiAdditional`}
                  path={`segments.coverageAmount.fields.${a.value}.ciAdditional`}
                  label={a.label}
              />
            </GridColumn>
          })}
        </>}
        <GridColumn width={16}>
          <Card title='Total Benefit Amounts'>
            <Message content={CONFIRM_TOTAL_AMOUNTS} />
            {life && <Grid container>
              <GridRow className='u-mT8'>
                <GridColumn width={16}>
                  <h5>{LIFE_AMOUNTS}</h5>
                </GridColumn>
                {applicants.filter(a => applicantType !== 'SP_ONLY' || a.value !== 'employee').map(a => {
                  return <GridColumn key={`${a.value}-total-life`} width={5}>
                    <CurrencyInput
                      id={`${a.value}LifeTotal`}
                      path={`segments.coverageAmount.fields.${a.value}.lifeTotal`}
                      disabled
                      label={`${a.label} Total`}
                    />
                  </GridColumn>
                })}
              </GridRow>
            </Grid>}
            {ci && <Grid container>
              <GridRow>
                <GridColumn width={16}>
                  <h5>{CI_AMOUNTS}</h5>
                </GridColumn>
                {applicants.filter(a => applicantType === 'SP_ONLY' ? a.value === 'spouse' : a.value !== 'children').map(a => {
                  return <GridColumn key={`${a.value}-total-ci`} width={5}>
                    <CurrencyInput
                      id={`${a.value}CiTotal`}
                      path={`segments.coverageAmount.fields.${a.value}.ciTotal`}
                      disabled
                      label={`${a.label} Total`}
                    />
                  </GridColumn>
                })}
              </GridRow>
            </Grid>}
          </Card>
        </GridColumn>
      </GridRow>
    </Grid>}
  </>)
}